"use client";
/* eslint-disable @next/next/no-img-element */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import type { CountrySelectorTranslations } from '../../i18n/translationProps';
import { isCategorySlug } from '../../i18n/categorySlugTranslations';
import { countryFlagSrc, getCountryName, filterBlacklistedCountries } from '../../helpers/countries';
import { useCardsService } from '../../hooks/useCardsService';
import { getCountryCookie, setCountryCookie, removeCountryCookie, COUNTRY_CHANGE_EVENT } from '../../helpers/countryCookie';
import { ExpandMoreIcon, CardGiftcardIcon, SearchIcon, CloseIcon } from '../icons';
import styles from './CountrySelector.module.css';

interface CountrySelectorProps {
  onCountryChange?: (country: string | null) => void;
  disabled?: boolean;
  translations: CountrySelectorTranslations;
  variant?: 'full';
}

export const CountrySelector: React.FC<CountrySelectorProps> = ({ onCountryChange, disabled, translations, variant }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { getCountries } = useCardsService();
  const t = translations;
  const [isOpen, setIsOpen] = useState(false);
  const [countries, setCountries] = useState<{ code: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const searchParams = useSearchParams();

  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    () => searchParams?.get('country')?.toUpperCase() || getCountryCookie() || null
  );

  useEffect(() => {
    setSelectedCountry(searchParams?.get('country')?.toUpperCase() || getCountryCookie() || null);
  }, [searchParams]);

  useEffect(() => {
    const handleCookieChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setSelectedCountry(searchParams?.get('country')?.toUpperCase() || detail || null);
    };
    window.addEventListener(COUNTRY_CHANGE_EVENT, handleCookieChange);
    return () => window.removeEventListener(COUNTRY_CHANGE_EVENT, handleCookieChange);
  }, [searchParams]);

  const updateUrlWithCountry = useCallback((countryCode: string | null): string => {
    const params = new URLSearchParams(searchParams?.toString() || '');

    if (countryCode) {
      params.set('country', countryCode.toUpperCase());
    } else {
      params.delete('country');
    }

    const queryString = params.toString();
    return queryString ? `${pathname}?${queryString}` : pathname || '/';
  }, [pathname, searchParams]);

  const { locale, getCanonical } = useLocalizedPath();

  const canonicalPath = useMemo(() => {
    if (!pathname) return '/';
    const segments = pathname.split('/').filter(Boolean);
    const pathWithoutLocale = segments[0] === locale ? '/' + segments.slice(1).join('/') || '/' : pathname;
    return getCanonical(pathWithoutLocale);
  }, [pathname, locale, getCanonical]);

  const isGiftcardsListingPage = useMemo(() => {
    if (canonicalPath === '/giftcards') return true;
    if (canonicalPath.startsWith('/giftcards/')) {
      const slug = canonicalPath.split('/')[2];
      return slug ? isCategorySlug(slug, locale) : false;
    }
    return false;
  }, [canonicalPath]);

  const handleCountrySelect = useCallback((countryCode: string | null) => {
    if (!pathname) return;

    const shouldUpdateUrl = canonicalPath === '/' || isGiftcardsListingPage;

    if (countryCode) {
      setCountryCookie(countryCode);
    } else {
      removeCountryCookie();
    }

    if (shouldUpdateUrl) {
      const newUrl = updateUrlWithCountry(countryCode);
      router.push(newUrl);
    }

    onCountryChange?.(countryCode);
    setIsOpen(false);
  }, [pathname, canonicalPath, isGiftcardsListingPage, router, onCountryChange, updateUrlWithCountry]);

  const openModal = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setSearchQuery('');
  }, [disabled]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  // Fetch countries from backend
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const countryCodes = await getCountries();
        if (countryCodes && countryCodes.length) {
          const filteredCountries = filterBlacklistedCountries(countryCodes);
          const formattedCountries = filteredCountries.map((countryCode: string) => ({
            code: countryCode,
            name: getCountryName(countryCode)
          }));
          setCountries(formattedCountries);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, [getCountries]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay to wait for animation
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeModal]);

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return countries;
    const query = searchQuery.toLowerCase().trim();
    return countries.filter(country =>
      country.name.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query)
    );
  }, [countries, searchQuery]);

  const selectedCountryName = selectedCountry ? getCountryName(selectedCountry) : null;

  return (
    <>
      {/* Trigger Button */}
      <div
        className={`${styles.countryWrapper}${disabled ? ` ${styles.disabled}` : ''}${variant === 'full' ? ` ${styles.full}` : ''}`}
        onClick={openModal}
        title={disabled ? 'Country filter only works on homepage and gift cards pages' : undefined}
      >
        <div className={styles.countryButton}>
          {selectedCountry ? (
            selectedCountry === 'WW' ? (
              <span className={styles.globeEmoji}>🌍</span>
            ) : (
              <img
                src={countryFlagSrc(selectedCountry)}
                alt={selectedCountry}
                width={20}
                height={15}
                style={{ width: '20px', height: '15px', borderRadius: '2px' }}
              />
            )
          ) : (
            <CardGiftcardIcon fontSize="small" className={styles.giftcardIcon} />
          )}
          <span className={styles.countryLabel}>
            {selectedCountryName ? `${selectedCountryName}` : t.region}
          </span>
          <ExpandMoreIcon
            fontSize="small"
            className={styles.expandIcon}
          />
        </div>
      </div>

      {/* Modal Popup */}
      {isOpen && (
        <div className={styles.overlay} onClick={closeModal}>
          <div
            ref={modalRef}
            className={styles.modal}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{t.region}</h3>
              <button className={styles.closeButton} onClick={closeModal} aria-label="Close">
                <CloseIcon fontSize="small" />
              </button>
            </div>

            {/* Search */}
            <div className={styles.searchWrapper}>
              <SearchIcon fontSize="small" className={styles.searchIcon} />
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder={t.searchRegion}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <CloseIcon fontSize="small" />
                </button>
              )}
            </div>

            {/* Country List */}
            <div className={styles.countryList}>
              {/* All Gift Cards option */}
              {!searchQuery && (
                <div
                  className={`${styles.countryOption}${!selectedCountry ? ` ${styles.selected}` : ''}`}
                  onClick={() => handleCountrySelect(null)}
                >
                  <div className={styles.countryInfo}>
                    <CardGiftcardIcon fontSize="small" className={styles.clearIcon} />
                    <span className={styles.countryName}>{t.allGiftCards}</span>
                  </div>
                  {!selectedCountry && <div className={styles.checkmark}>✓</div>}
                </div>
              )}

              {loading ? (
                <div className={styles.loadingText}>{t.loadingCountries}</div>
              ) : filteredCountries.length === 0 ? (
                <div className={styles.emptyText}>No results</div>
              ) : (
                filteredCountries.map((country) => (
                  <div
                    key={country.code}
                    className={`${styles.countryOption}${selectedCountry === country.code ? ` ${styles.selected}` : ''}`}
                    onClick={() => handleCountrySelect(country.code)}
                  >
                    <div className={styles.countryInfo}>
                      {country.code === 'WW' ? (
                        <span className={styles.globeEmoji}>🌍</span>
                      ) : (
                        <img
                          src={countryFlagSrc(country.code)}
                          alt={country.name}
                          loading="lazy"
                          width={20}
                          height={15}
                          style={{ width: '20px', height: '15px', borderRadius: '2px' }}
                        />
                      )}
                      <span className={`${styles.countryName}${selectedCountry === country.code ? ` ${styles.selectedName}` : ''}`}>
                        {country.name}
                      </span>
                      <span className={styles.countryCode}>{country.code}</span>
                    </div>
                    {selectedCountry === country.code && <div className={styles.checkmark}>✓</div>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
